import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { testi } from "./data/live";
import bookingSeo from "./data/bookingSeo.json";

const baseTranslations = {
  it: {
    nav: {
      home: "Home",
      units: "Le Unità",
      surroundings: "Dintorni",
      reviews: "Recensioni",
      book: "Prenota",
      where: "Dove Siamo",
      contact: "Contatti",
      menuOpen: "Apri il menu",
      menuClose: "Chiudi il menu",
      skip: "Salta al contenuto",
    },
    gallery: { close: "Chiudi", prev: "Foto precedente", next: "Foto successiva" },
    notFound: { title: "Pagina non trovata", text: "L'indirizzo che hai aperto non esiste o è stato spostato.", cta: "Torna alla home", seoTitle: "Pagina non trovata | Postumia Place" },
    hero: {
      overline: "B&B · Postioma di Paese · Treviso",
      lines: ["Un soggiorno tranquillo", "a Postioma di Paese, tra Treviso e Venezia"],
      sub: "Appartamenti indipendenti nella quiete della campagna trevigiana: a 12 minuti da Treviso, un'ora di treno da Venezia, le Dolomiti all'orizzonte.",
      ctaPrimary: "Prenota il tuo soggiorno",
      ctaSecondary: "Scopri le unità",
      scroll: "Scorri",
    },
    marquee: ["Riposo", "Scoperta", "Sapori", "Natura", "Ospitalità"],
    stats: [
      { value: "4.8+", label: "valutazione media" },
      { value: "260", label: "recensioni verificate" },
      { value: "9", label: "anni di ospitalità" },
    ],
    homeUnits: {
      overline: "Gli appartamenti",
      title: "Scegli dove stare",
      all: "Vedi tutti",
      discover: "Scopri l'appartamento",
      prev: "Appartamenti precedenti",
      next: "Appartamenti successivi",
      verified: "Recensione verificata",
      reviews: "Le recensioni degli ospiti",
      prevReview: "Recensione precedente",
      nextReview: "Recensione successiva",
    },
    manifesto: {
      overline: "Perché Postumia Place",
      title: "Tre ragioni per restare",
      chapters: [
        {
          n: "01",
          title: "La filosofia",
          text: "Appartamenti indipendenti, con ingresso proprio e senza orari di reception. Spazi luminosi, parquet e la tranquillità della campagna veneta.",
        },
        {
          n: "02",
          title: "La posizione",
          text: "La stazione ferroviaria a 500 metri. Treviso a 12 minuti, Venezia a un'ora di treno, le colline del Prosecco a mezz'ora. Il Veneto intero, a portata di mano.",
        },
        {
          n: "03",
          title: "L'ospitalità",
          text: "Elio accoglie ogni ospite di persona, da 9 anni. Check-in flessibile, risposta rapida e consigli sinceri su cosa vedere e dove mangiare.",
        },
      ],
    },
    ecoBadge: {
      enabled: true,
      text: "100% energia fotovoltaica",
    },
    unitsSection: {
      overline: "Le unità",
      title: "I nostri alloggi a Postioma di Paese",
      discover: "Scopri di più",
      guests: "ospiti",
      bedrooms: "camere",
      beds: "letti",
      bathrooms: "bagno",
      studio: "monolocale",
    },
    reviewTeaser: {
      overline: "Dicono di noi",
      quote: "Ci siamo sentiti davvero a casa.",
      author: "Rawane · giugno 2025",
      cta: "Leggi tutte le recensioni",
    },
    homeCta: {
      title: "Scrivici per conoscere la disponibilità",
      sub: "Richiedi le tue date: rispondiamo il prima possibile, senza impegno.",
      cta: "Verifica la disponibilità",
    },
    unitPage: {
      detailsTitle: "Nel dettaglio",
      fromPrice: "da {price} € a notte",
      overline: "B&B · Postioma di Paese",
      reviews: "recensioni",
      bookThis: "Prenota questa unità",
      galleryTitle: "La galleria",
      amenitiesTitle: "Cosa troverai",
      infoTitle: "Buono a sapersi",
      checkin: "Check-in",
      checkout: "Check-out",
      checkinValue: "dalle 15:00 alle 20:00",
      checkoutValue: "entro le 11:00",
      maxGuests: "Ospiti massimi",
      selfCheckin: "Arrivo autonomo",
      selfCheckinValue: "Self check-in semplice",
      nearbyTitle: "A pochi passi",
      nearby: [
        "Market di fronte casa",
        "Farmacia a 250 m",
        "Pizzeria e ristorante a 50 m",
        "Stazione ferroviaria a 500 m",
        "Fermata bus a 5 minuti",
        "Poste e bancomat in paese",
      ],
      otherUnit: "Scopri anche",
      sleepTitle: "Dove dormirai",
      accessTitle: "Accesso per gli ospiti",
      notesTitle: "Altre cose da tenere a mente",
      reviewsTitle: "Le recensioni degli ospiti",
      verified: "da Airbnb",
      showMore: "Mostra tutte le recensioni",
      showLess: "Mostra meno",
      showAllAmenities: "Mostra tutti i servizi ·",
      showLessAmenities: "Mostra meno",
      notAvailableTitle: "Non disponibili in questo alloggio",
      categories: {
        cleanliness: "Pulizia",
        accuracy: "Precisione",
        checkin: "Check-in",
        communication: "Comunicazione",
        location: "Posizione",
        value: "Qualità/prezzo",
      },
      guests: "ospiti",
      bedrooms: "camere da letto",
      beds: "letti",
      bathrooms: "bagno",
      sqm: "m²",
      unavailable: "Non disponibile",
      comingSoon: "Presto disponibile",
    },
    unitStatus: {
      available: { label: "Disponibile", hint: "Prenotabile online" },
      comingSoon: { label: "Presto disponibile", hint: "Non ancora prenotabile" },
      unavailable: { label: "Non disponibile", hint: "Al momento non prenotabile" },
    },
    dintorni: {
      overline: "I dintorni",
      title: "Il Veneto da vivere",
      sub: "Postioma è un punto di partenza perfetto: città d'arte, colline del Prosecco, borghi e montagne. E la sera si torna nella quiete del paese.",
      practicalTitle: "Nel paese, a piedi",
      stationNote: "stazione a 500 m",
      practical: [
        "Market di fronte casa",
        "Farmacia a 250 metri",
        "Pizzeria e ristorante a 50 metri",
        "Ufficio postale e bancomat",
        "Stazione ferroviaria a 500 m",
        "Bus per Treviso, Montebelluna e Valdobbiadene",
      ],
      places: [
        {
          name: "Treviso",
          time: "12 min",
          text: "Città d'acqua e di portici: i canali dei Buranelli, Piazza dei Signori, il mercato del pesce e l'aperitivo con il Prosecco DOC nelle osterie del centro.",
        },
        {
          name: "Venezia",
          time: "1 h di treno",
          text: "Dalla stazione di Postioma il treno porta direttamente a Venezia Santa Lucia. La Serenissima per un giorno, senza pensieri di parcheggio.",
        },
        {
          name: "Colline del Prosecco",
          time: "30 min",
          text: "Conegliano e Valdobbiadene, patrimonio UNESCO: cantine, strade del vino e colline che sembrano dipinte. Degustazioni ovunque.",
        },
        {
          name: "Asolo e i borghi",
          time: "30 min",
          text: "Asolo, la città dei cento orizzonti, e l'Abbazia di Follina: gioielli a mezz'ora di strada, tra ville venete e paesaggi da cartolina.",
        },
        {
          name: "Dolomiti",
          time: "1 h",
          text: "Il Parco Nazionale delle Dolomiti Bellunesi è a un'ora d'auto: trekking, rifugi e panorami infiniti per una giornata di montagna.",
        },
      ],
      cta: "Prenota la tua base nel Veneto",
    },
    reviewsPage: {
      overline: "Le recensioni",
      title: "Parole vere di ospiti veri",
      sub: "Recensioni pubblicate dai nostri ospiti su Airbnb negli ultimi anni.",
      verified: "da Airbnb",
      showMore: "Mostra tutte le recensioni",
      showLess: "Mostra meno",
      cards: [
        { name: "Postumia Place", rating: "4.83", count: "47 recensioni" },
        { name: "Postumia Place Junior", rating: "4.81", count: "213 recensioni" },
        { name: "Elio · il tuo host", rating: "4.82", count: "260 recensioni" },
      ],
      reviews: [
        {
          name: "R.",
          meta: "Postumia Place · giugno 2025",
          text: "Abbiamo trascorso alcuni giorni meravigliosi in questo B&B e non possiamo che consigliarlo! La struttura è accogliente, curata nei dettagli e pulita. La camera era spaziosa, silenziosa e dotata di tutto il necessario. Ci siamo sentiti davvero a casa.",
        },
        {
          name: "C.",
          meta: "Postumia Place Junior · marzo 2026",
          text: "La zona è estremamente tranquilla, perfetta per rilassarsi lontano dal caos. L'appartamento è ampio e ben organizzato, ideale anche per famiglie. Il proprietario è molto disponibile e la flessibilità sul check-in ha reso tutto senza stress. Lo consigliamo senza esitazione.",
        },
        {
          name: "T.",
          meta: "Postumia Place · giugno 2025",
          text: "Ottima soluzione sia per lavoro che per vacanza, tutto come da descrizione e foto, anzi meglio. Comodissimo come base per visitare Venezia, Treviso e Padova. Il paese è tranquillo e c'è il posto auto. Al prossimo viaggio a Treviso tornerò di sicuro.",
        },
        {
          name: "C.",
          meta: "Postumia Place Junior · giugno 2026",
          text: "Casa accogliente e completa: c'era tutto il necessario, dalla cucina al bagno. Accoglienza gentilissima e ottimi consigli su cosa vedere nei dintorni. Posizione perfetta: 15 minuti da Treviso e 30 da Conegliano e Asolo. Non perdete l'abbazia di Follina.",
        },
        {
          name: "R.",
          meta: "Postumia Place · dicembre 2025",
          text: "Ambiente confortevole, la persona di riferimento è estremamente cordiale e disponibile.",
        },
        {
          name: "D. J.",
          meta: "Postumia Place · 2026",
          text: "L'alloggio è stato perfetto per il mio soggiorno: Elio ha risposto a tutte le mie domande e il check-in è possibile a qualsiasi ora. Lo consiglio e tornerei ogni volta.",
        },
        {
          name: "C.",
          meta: "Postumia Place · luglio 2026",
          text: "Tutto perfetto! Assolutamente consigliato.",
        },
        {
          name: "K.",
          meta: "Postumia Place Junior · 2026",
          text: "È stato tutto ottimo. Sarei felice di tornare un giorno.",
        },
        {
          name: "M. A.",
          meta: "Postumia Place Junior · giugno 2026",
          text: "Grazie Elio, è andato tutto bene: tornerò sicuramente!",
        },
        {
          name: "D. J.",
          meta: "Postumia Place Junior · 2026",
          text: "Posto molto tranquillo, host molto cordiale. Lo consiglio.",
        },
        {
          name: "S.",
          meta: "Postumia Place Junior · 2026",
          text: "Grazie mille Elio!",
        },
      ],
      cta: "Vieni a scrivere la prossima recensione",
    },
    unitsPage: {
      overline: "Le unità",
      title: "I nostri alloggi a Postioma di Paese",
      sub: "Appartamenti indipendenti nella stessa casa di Postioma, con cortile, giardino e parcheggio riservato. Per coppie, famiglie e chi viaggia per lavoro.",
      discover: "Scopri l'unità",
      guests: "ospiti",
      bedrooms: "camere",
      beds: "letti",
      bathrooms: "bagno",
      studio: "monolocale",
      sqm: "m²",
    },
    contactPage: {
      overline: "B&B · Postioma di Paese",
      title: "Contatti",
      sub: "Domande, richieste speciali, soggiorni di lavoro o lunghi periodi: scrivici, rispondiamo il prima possibile.",
      form: {
        name: "Nome e cognome",
        email: "Email",
        message: "Messaggio",
        messagePlaceholder: "Come possiamo aiutarti?",
        submit: "Invia messaggio",
        sending: "Invio in corso…",
      },
      success: {
        title: "Messaggio inviato!",
        text: "Grazie {name}: ti risponderemo il prima possibile a {email}.",
        newMessage: "Invia un altro messaggio",
      },
      errors: {
        name: "Inserisci il tuo nome.",
        email: "Inserisci un'email valida.",
        message: "Scrivi un messaggio.",
        generic: "Qualcosa è andato storto. Riprova o scrivici direttamente.",
      },
      infoTitle: "I nostri contatti",
      response: "Risposta rapida · 9 anni di ospitalità",
    },
    wherePage: {
      overline: "Dove siamo",
      title: "A 12 minuti da Treviso",
      sub: "Postumia Place si trova a Postioma di Paese, in provincia di Treviso: un paese tranquillo con tutti i servizi a pochi passi e il Veneto intero a portata di mano.",
      addressTitle: "L'indirizzo",
      address: "Via Fratelli Bianchin 13 · 31038 Postioma di Paese (TV) · Veneto, Italia",
      howTitle: "Come arrivare",
      how: [
        { icon: "Plane", title: "In aereo", text: "Aeroporto di Treviso Canova a 15 minuti d'auto; Venezia Marco Polo a circa 30 minuti." },
        { icon: "TrainFront", title: "In treno", text: "La stazione ferroviaria di Postioma è a 500 metri: Treviso in pochi minuti, Venezia in circa un'ora." },
        { icon: "Car", title: "In auto", text: "A circa 10 minuti dalle uscite di Treviso. Parcheggio interno gratuito e riservato agli ospiti." },
      ],
      distancesTitle: "Le distanze",
      distances: ["Treviso · 12 min", "Aeroporto TSF · 15 min", "Venezia · 1 h", "Conegliano · 30 min", "Asolo · 30 min", "Dolomiti · 1 h"],
      mapLabel: "Mappa di Postioma di Paese",
    },
    booking: {
      overline: "Prenotazione diretta",
      title: "Prenota il tuo soggiorno",
      seo: bookingSeo.it,
      sub: "Invia la richiesta e ti risponderemo il prima possibile.",
      howTitle: "Come funziona",
      steps: [
        "Scegli l'unità e le date",
        "Invia la richiesta senza impegno",
        "Ti confermiamo la disponibilità",
      ],
      contactTitle: "Contatti diretti",
      times: "Check-in dalle 15:00 alle 20:00 · Check-out entro le 11:00",
      form: {
        unit: "Scegli l'unità",
        dates: "Le tue date",
        datesHint: "Seleziona check-in e check-out",
        arrival: "Arrivo",
        departure: "Partenza",
        pickArrival: "Tocca il giorno di arrivo",
        pickDeparture: "Ora tocca il giorno di partenza",
        changeDates: "Cambia date",
        legendStay: "Il tuo soggiorno",
        legendBooked: "Già occupato",
        notChosen: "Da scegliere",
        nights: "notti",
        night: "notte",
        priceTitle: "Il tuo prezzo",
        priceTotal: "Totale",
        guests: "Ospiti",
        maxGuests: "max",
        name: "Nome e cognome",
        namePlaceholder: "Mario Rossi",
        email: "Email",
        emailPlaceholder: "mario@esempio.it",
        phone: "Telefono / WhatsApp (facoltativo)",
        phonePlaceholder: "+39 340 000 0000",
        message: "Messaggio (facoltativo)",
        messagePlaceholder: "Orario di arrivo, esigenze particolari, domande…",
        submit: "Invia richiesta",
        prevMonth: "Mese precedente",
        nextMonth: "Mese successivo",
        guestsLess: "Togli un ospite",
        guestsMore: "Aggiungi un ospite",
        sending: "Invio in corso…",
      },
      errors: {
        dates: "Seleziona le date del soggiorno.",
        rangeBlocked: "Il periodo scelto include date già occupate: scegli un altro check-out o altre date.",
        name: "Inserisci il tuo nome.",
        email: "Inserisci un'email valida.",
        unavailable: "Queste date non sono più disponibili per l'unità scelta. Prova altre date o l'altra unità.",
        generic: "Qualcosa è andato storto. Riprova o scrivici direttamente.",
      },
      success: {
        title: "Richiesta inviata!",
        text: "Grazie {name}: abbiamo ricevuto la tua richiesta per {unit}. Ti risponderemo il prima possibile a {email} per confermare la disponibilità.",
        summary: "Il tuo riepilogo",
        guests: "ospiti",
        newRequest: "Invia un'altra richiesta",
      },
    },
    footer: {
      tagline: "Un cortile, la campagna veneta intorno. A 12 minuti da Treviso, a un'ora da Venezia.",
      contacts: "Contatti",
      explore: "Esplora",
      unitsTitle: "Le unità",
      address: "Via Fratelli Bianchin 13 · Postioma di Paese (TV) · Veneto · Italia",
      cin: "CIN: IT026055B4BEMQDC2F",
      rights: "Tutti i diritti riservati.",
      email: "info@postumiaplace.it",
      phone: "+39 349 418 4040",
      whatsapp: "+39 349 418 4040",
    },
  },
  en: {
    nav: {
      home: "Home",
      units: "The Units",
      surroundings: "Surroundings",
      reviews: "Reviews",
      book: "Book now",
      where: "Where We Are",
      contact: "Contact",
      menuOpen: "Open menu",
      menuClose: "Close menu",
      skip: "Skip to content",
    },
    gallery: { close: "Close", prev: "Previous photo", next: "Next photo" },
    notFound: { title: "Page not found", text: "The address you opened doesn't exist or has moved.", cta: "Back to the home page", seoTitle: "Page not found | Postumia Place" },
    hero: {
      overline: "B&B · Postioma di Paese · Treviso",
      lines: ["A quiet stay", "in Postioma di Paese, between Treviso and Venice"],
      sub: "Independent apartments in the quiet Treviso countryside: 12 minutes from Treviso, one hour by train from Venice, the Dolomites on the horizon.",
      ctaPrimary: "Book your stay",
      ctaSecondary: "Discover the units",
      scroll: "Scroll",
    },
    marquee: ["Rest", "Discover", "Savour", "Nature", "Hospitality"],
    stats: [
      { value: "4.8+", label: "average rating" },
      { value: "260", label: "verified reviews" },
      { value: "9", label: "years of hosting" },
    ],
    homeUnits: {
      overline: "The apartments",
      title: "Choose where to stay",
      all: "See all",
      discover: "Discover the apartment",
      prev: "Previous apartments",
      next: "Next apartments",
      verified: "Verified review",
      reviews: "Guest reviews",
      prevReview: "Previous review",
      nextReview: "Next review",
    },
    manifesto: {
      overline: "Why Postumia Place",
      title: "Three reasons to stay",
      chapters: [
        {
          n: "01",
          title: "The philosophy",
          text: "Independent apartments, with your own entrance and no reception hours. Bright spaces, parquet floors and the quiet of the Veneto countryside.",
        },
        {
          n: "02",
          title: "The location",
          text: "The train station is 500 metres away. Treviso in 12 minutes, Venice in one hour by train, the Prosecco hills in thirty. All of Veneto within easy reach.",
        },
        {
          n: "03",
          title: "The hospitality",
          text: "Elio has welcomed every guest in person for 9 years. Flexible check-in, quick replies and honest advice on what to see and where to eat.",
        },
      ],
    },
    ecoBadge: {
      enabled: true,
      text: "100% solar power",
    },
    unitsSection: {
      overline: "The units",
      title: "Our apartments in Postioma di Paese",
      discover: "Discover more",
      guests: "guests",
      bedrooms: "bedrooms",
      beds: "beds",
      bathrooms: "bathroom",
      studio: "studio flat",
    },
    reviewTeaser: {
      overline: "Word of mouth",
      quote: "We truly felt at home.",
      author: "Rawane · June 2025",
      cta: "Read all reviews",
    },
    homeCta: {
      title: "Write to us to check availability",
      sub: "Request your dates: we reply as soon as we can, no commitment.",
      cta: "Check availability",
    },
    unitPage: {
      detailsTitle: "In detail",
      fromPrice: "from €{price} a night",
      overline: "B&B · Postioma di Paese",
      reviews: "reviews",
      bookThis: "Book this unit",
      galleryTitle: "The gallery",
      amenitiesTitle: "What you'll find",
      infoTitle: "Good to know",
      checkin: "Check-in",
      checkout: "Check-out",
      checkinValue: "3 pm to 8 pm",
      checkoutValue: "by 11 am",
      maxGuests: "Max guests",
      selfCheckin: "Easy arrival",
      selfCheckinValue: "Simple self check-in",
      nearbyTitle: "A short walk away",
      nearby: [
        "Grocery store across the street",
        "Pharmacy 250 m away",
        "Pizzeria & restaurant 50 m away",
        "Train station 500 m away",
        "Bus stop 5 minutes away",
        "Post office & ATM in the village",
      ],
      otherUnit: "Also discover",
      sleepTitle: "Where you'll sleep",
      accessTitle: "Guest access",
      notesTitle: "Other things to note",
      reviewsTitle: "Guest reviews",
      verified: "via Airbnb",
      showMore: "Show all reviews",
      showLess: "Show less",
      showAllAmenities: "Show all amenities ·",
      showLessAmenities: "Show less",
      notAvailableTitle: "Not included in this place",
      categories: {
        cleanliness: "Cleanliness",
        accuracy: "Accuracy",
        checkin: "Check-in",
        communication: "Communication",
        location: "Location",
        value: "Value",
      },
      guests: "guests",
      bedrooms: "bedrooms",
      beds: "beds",
      bathrooms: "bathroom",
      sqm: "m²",
      unavailable: "Not available",
      comingSoon: "Coming soon",
    },
    unitStatus: {
      available: { label: "Available", hint: "Book online" },
      comingSoon: { label: "Coming soon", hint: "Not bookable yet" },
      unavailable: { label: "Not available", hint: "Not bookable at the moment" },
    },
    dintorni: {
      overline: "Surroundings",
      title: "Veneto, up close",
      sub: "Postioma is the perfect starting point: art cities, Prosecco hills, villages and mountains. And in the evening, you return to the quiet of the countryside.",
      practicalTitle: "In the village, on foot",
      stationNote: "station 500 m away",
      practical: [
        "Grocery store across the street",
        "Pharmacy 250 metres away",
        "Pizzeria & restaurant 50 metres away",
        "Post office and ATM",
        "Train station 500 m away",
        "Buses to Treviso, Montebelluna & Valdobbiadene",
      ],
      places: [
        {
          name: "Treviso",
          time: "12 min",
          text: "A city of water and arcades: the Buranelli canals, Piazza dei Signori, the fish market and a Prosecco DOC aperitivo in the old-town taverns.",
        },
        {
          name: "Venice",
          time: "1 h by train",
          text: "From Postioma station, the train takes you straight to Venezia Santa Lucia. The Serenissima for a day, with no parking worries.",
        },
        {
          name: "Prosecco Hills",
          time: "30 min",
          text: "Conegliano and Valdobbiadene, a UNESCO World Heritage site: wineries, wine roads and hills that look painted. Tastings everywhere.",
        },
        {
          name: "Asolo & the villages",
          time: "30 min",
          text: "Asolo, the town of a hundred horizons, and Follina Abbey: gems half an hour away, among Venetian villas and postcard landscapes.",
        },
        {
          name: "The Dolomites",
          time: "1 h",
          text: "The Dolomiti Bellunesi National Park is an hour's drive: hiking trails, mountain huts and endless views for a day in the mountains.",
        },
      ],
      cta: "Book your base in Veneto",
    },
    reviewsPage: {
      overline: "Reviews",
      title: "True words from real guests",
      sub: "Reviews published by our guests on Airbnb over the years.",
      verified: "via Airbnb",
      cards: [
        { name: "Postumia Place", rating: "4.83", count: "47 reviews" },
        { name: "Postumia Place Junior", rating: "4.81", count: "213 reviews" },
        { name: "Elio · your host", rating: "4.82", count: "260 reviews" },
      ],
      reviews: [
        {
          name: "R.",
          meta: "Postumia Place · June 2025",
          text: "We spent some wonderful days at this B&B and can only recommend it! The property is welcoming, detailed and clean. The room was spacious, quiet and had everything we needed. We truly felt at home.",
        },
        {
          name: "C.",
          meta: "Postumia Place Junior · March 2026",
          text: "The area is extremely quiet, perfect for relaxing away from the chaos. The apartment is spacious and well organised, ideal for families too. The owner is very helpful and the check-in flexibility made everything stress-free. We recommend it without hesitation.",
        },
        {
          name: "T.",
          meta: "Postumia Place · June 2025",
          text: "A great solution for both work and holiday: everything as described and pictured, actually better. Very convenient as a base for Venice, Treviso and Padua. The village is quiet and there's parking. I'll definitely be back on my next trip to Treviso.",
        },
        {
          name: "C.",
          meta: "Postumia Place Junior · June 2026",
          text: "A cosy, fully equipped home: everything we needed, from kitchen to bathroom. A very kind welcome and great tips on what to see nearby. Perfect location: 15 minutes from Treviso and 30 from Conegliano and Asolo. Don't miss Follina Abbey.",
        },
        {
          name: "R.",
          meta: "Postumia Place · December 2025",
          text: "Comfortable environment, the host is extremely friendly and helpful.",
        },
        {
          name: "D. J.",
          meta: "Postumia Place · 2026",
          text: "The accommodation was perfect for my stay: Elio answered all my questions and check-in is possible at any time. I recommend it and would return every time.",
        },
        {
          name: "C.",
          meta: "Postumia Place · July 2026",
          text: "Everything perfect! Absolutely recommended.",
        },
        {
          name: "K.",
          meta: "Postumia Place Junior · 2026",
          text: "Everything was great. I would be happy to come back one day.",
        },
        {
          name: "M. A.",
          meta: "Postumia Place Junior · June 2026",
          text: "Thank you Elio, everything went well: I will definitely be back!",
        },
        {
          name: "D. J.",
          meta: "Postumia Place Junior · 2026",
          text: "A very quiet place, very friendly host. I recommend it.",
        },
        {
          name: "S.",
          meta: "Postumia Place Junior · 2026",
          text: "Thank you so much Elio!",
        },
      ],
      cta: "Come and write the next review",
    },
    unitsPage: {
      overline: "The units",
      title: "Our apartments in Postioma di Paese",
      sub: "Independent apartments in the same house in Postioma, with courtyard, garden and reserved parking. For couples, families and business travellers.",
      discover: "Discover the unit",
      guests: "guests",
      bedrooms: "bedrooms",
      beds: "beds",
      bathrooms: "bathroom",
      studio: "studio flat",
      sqm: "m²",
    },
    contactPage: {
      overline: "B&B · Postioma di Paese",
      title: "Contact",
      sub: "Questions, special requests, business or long stays: write to us, we reply as soon as we can.",
      form: {
        name: "Full name",
        email: "Email",
        message: "Message",
        messagePlaceholder: "How can we help you?",
        submit: "Send message",
        sending: "Sending…",
      },
      success: {
        title: "Message sent!",
        text: "Thank you {name}: we'll reply as soon as we can at {email}.",
        newMessage: "Send another message",
      },
      errors: {
        name: "Please enter your name.",
        email: "Please enter a valid email.",
        message: "Please write a message.",
        generic: "Something went wrong. Please try again or contact us directly.",
      },
      infoTitle: "Our contacts",
      response: "Quick replies · 9 years of hosting",
    },
    wherePage: {
      overline: "Where we are",
      title: "12 minutes from Treviso",
      sub: "Postumia Place is in Postioma di Paese, in the province of Treviso: a quiet village with every service a short walk away and all of Veneto within easy reach.",
      addressTitle: "The address",
      address: "Via Fratelli Bianchin 13 · 31038 Postioma di Paese (TV) · Veneto, Italy",
      howTitle: "Getting here",
      how: [
        { icon: "Plane", title: "By plane", text: "Treviso Canova Airport is 15 minutes by car; Venice Marco Polo about 30 minutes." },
        { icon: "TrainFront", title: "By train", text: "Postioma train station is 500 metres away: Treviso in a few minutes, Venice in about an hour." },
        { icon: "Car", title: "By car", text: "About 10 minutes from the Treviso motorway exits. Free reserved parking for guests." },
      ],
      distancesTitle: "Distances",
      distances: ["Treviso · 12 min", "TSF Airport · 15 min", "Venice · 1 h", "Conegliano · 30 min", "Asolo · 30 min", "Dolomites · 1 h"],
      mapLabel: "Map of Postioma di Paese",
    },
    booking: {
      overline: "Direct booking",
      title: "Book your stay",
      seo: bookingSeo.en,
      sub: "Send your request and we'll reply as soon as we can.",
      howTitle: "How it works",
      steps: [
        "Choose the unit and dates",
        "Send your request, with no commitment",
        "We confirm availability",
      ],
      contactTitle: "Direct contacts",
      times: "Check-in 3 pm to 8 pm · Check-out by 11 am",
      form: {
        unit: "Choose the unit",
        dates: "Your dates",
        datesHint: "Select check-in and check-out",
        arrival: "Arrival",
        departure: "Departure",
        pickArrival: "Tap your arrival day",
        pickDeparture: "Now tap your departure day",
        changeDates: "Change dates",
        legendStay: "Your stay",
        legendBooked: "Already booked",
        notChosen: "Not chosen yet",
        nights: "nights",
        night: "night",
        priceTitle: "Your price",
        priceTotal: "Total",
        guests: "Guests",
        maxGuests: "max",
        name: "Full name",
        namePlaceholder: "John Smith",
        email: "Email",
        emailPlaceholder: "john@example.com",
        phone: "Phone / WhatsApp (optional)",
        phonePlaceholder: "+44 7000 000000",
        message: "Message (optional)",
        messagePlaceholder: "Arrival time, special needs, questions…",
        submit: "Send request",
        prevMonth: "Previous month",
        nextMonth: "Next month",
        guestsLess: "Remove a guest",
        guestsMore: "Add a guest",
        sending: "Sending…",
      },
      errors: {
        dates: "Please select your stay dates.",
        rangeBlocked: "The selected period includes dates that are already booked: choose a different check-out or other dates.",
        name: "Please enter your name.",
        email: "Please enter a valid email.",
        unavailable: "These dates are no longer available for the chosen unit. Try different dates or the other unit.",
        generic: "Something went wrong. Please try again or contact us directly.",
      },
      success: {
        title: "Request sent!",
        text: "Thank you {name}: we've received your request for {unit}. We'll reply as soon as we can at {email} to confirm availability.",
        summary: "Your summary",
        guests: "guests",
        newRequest: "Send another request",
      },
    },
    footer: {
      tagline: "A courtyard, the Veneto countryside all around. 12 minutes from Treviso, one hour from Venice.",
      contacts: "Contacts",
      explore: "Explore",
      unitsTitle: "The units",
      address: "Via Fratelli Bianchin 13 · Postioma di Paese (TV) · Veneto · Italy",
      cin: "CIN: IT026055B4BEMQDC2F",
      rights: "All rights reserved.",
      email: "info@postumiaplace.it",
      phone: "+39 349 418 4040",
      whatsapp: "+39 349 418 4040",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      units: "Les Logements",
      surroundings: "Environs",
      reviews: "Avis",
      book: "Réserver",
      where: "Où Nous Sommes",
      contact: "Contact",
      menuOpen: "Ouvrir le menu",
      menuClose: "Fermer le menu",
      skip: "Aller au contenu",
    },
    gallery: { close: "Fermer", prev: "Photo précédente", next: "Photo suivante" },
    notFound: { title: "Page introuvable", text: "L'adresse que vous avez ouverte n'existe pas ou a été déplacée.", cta: "Retour à l'accueil", seoTitle: "Page introuvable | Postumia Place" },
    hero: {
      overline: "B&B · Postioma di Paese · Trévise",
      lines: ["Un séjour tranquille", "à Postioma di Paese, entre Trévise et Venise"],
      sub: "Appartements indépendants dans le calme de la campagne trévisane : à 12 minutes de Trévise, une heure de train de Venise, les Dolomites à l'horizon.",
      ctaPrimary: "Réservez votre séjour",
      ctaSecondary: "Découvrez les logements",
      scroll: "Défiler",
    },
    marquee: ["Repos", "Découverte", "Saveurs", "Nature", "Hospitalité"],
    stats: [
      { value: "4.8+", label: "note moyenne" },
      { value: "260", label: "avis vérifiés" },
      { value: "9", label: "ans d'hospitalité" },
    ],
    homeUnits: {
      overline: "Les appartements",
      title: "Choisissez où séjourner",
      all: "Tout voir",
      discover: "Découvrir l'appartement",
      prev: "Appartements précédents",
      next: "Appartements suivants",
      verified: "Avis vérifié",
      reviews: "Avis des voyageurs",
      prevReview: "Avis précédent",
      nextReview: "Avis suivant",
    },
    manifesto: {
      overline: "Pourquoi Postumia Place",
      title: "Trois raisons de rester",
      chapters: [
        {
          n: "01",
          title: "La philosophie",
          text: "Appartements indépendants, avec entrée privée et sans horaires de réception. Espaces lumineux, parquet et le calme de la campagne vénitienne.",
        },
        {
          n: "02",
          title: "La position",
          text: "La gare ferroviaire à 500 mètres. Trévise en 12 minutes, Venise en une heure de train, les collines du Prosecco en trente minutes. Toute la Vénétie à portée de main.",
        },
        {
          n: "03",
          title: "L'hospitalité",
          text: "Elio accueille chaque invité en personne depuis 9 ans. Arrivée flexible, réponse rapide et des conseils sincères sur quoi voir et où manger.",
        },
      ],
    },
    ecoBadge: {
      enabled: true,
      text: "100 % énergie solaire",
    },
    unitsSection: {
      overline: "Les logements",
      title: "Nos logements à Postioma di Paese",
      discover: "En savoir plus",
      guests: "invités",
      bedrooms: "chambres",
      beds: "lits",
      bathrooms: "salle de bain",
      studio: "studio",
    },
    reviewTeaser: {
      overline: "Ils parlent de nous",
      quote: "Nous nous sommes vraiment sentis chez nous.",
      author: "Rawane · juin 2025",
      cta: "Lire tous les avis",
    },
    homeCta: {
      title: "Écrivez-nous pour connaître les disponibilités",
      sub: "Demandez vos dates : nous répondons dans les plus brefs délais, sans engagement.",
      cta: "Vérifier les disponibilités",
    },
    unitPage: {
      detailsTitle: "En détail",
      fromPrice: "dès {price} € la nuit",
      overline: "B&B · Postioma di Paese",
      reviews: "avis",
      bookThis: "Réservez ce logement",
      galleryTitle: "La galerie",
      amenitiesTitle: "Ce que vous trouverez",
      infoTitle: "Bon à savoir",
      checkin: "Arrivée",
      checkout: "Départ",
      checkinValue: "de 15h00 à 20h00",
      checkoutValue: "avant 11h00",
      maxGuests: "Invités maximum",
      selfCheckin: "Arrivée autonome",
      selfCheckinValue: "Auto check-in simple",
      nearbyTitle: "À deux pas",
      nearby: [
        "Épicerie en face de la maison",
        "Pharmacie à 250 m",
        "Pizzeria et restaurant à 50 m",
        "Gare ferroviaire à 500 m",
        "Arrêt de bus à 5 minutes",
        "Poste et distributeur au village",
      ],
      otherUnit: "Découvrez aussi",
      sleepTitle: "Où vous dormirez",
      accessTitle: "Accès pour les invités",
      notesTitle: "Autres choses à savoir",
      reviewsTitle: "Les avis des invités",
      verified: "via Airbnb",
      showMore: "Voir tous les avis",
      showLess: "Voir moins",
      showAllAmenities: "Voir tous les équipements ·",
      showLessAmenities: "Voir moins",
      notAvailableTitle: "Non disponibles dans ce logement",
      categories: {
        cleanliness: "Propreté",
        accuracy: "Précision",
        checkin: "Arrivée",
        communication: "Communication",
        location: "Emplacement",
        value: "Qualité/prix",
      },
      guests: "invités",
      bedrooms: "chambres",
      beds: "lits",
      bathrooms: "salle de bain",
      sqm: "m²",
      unavailable: "Non disponible",
      comingSoon: "Bientôt disponible",
    },
    unitStatus: {
      available: { label: "Disponible", hint: "Réservable en ligne" },
      comingSoon: { label: "Bientôt disponible", hint: "Pas encore réservable" },
      unavailable: { label: "Non disponible", hint: "Non réservable pour le moment" },
    },
    dintorni: {
      overline: "Les environs",
      title: "La Vénétie à vivre",
      sub: "Postioma est un point de départ parfait : villes d'art, collines du Prosecco, villages et montagnes. Et le soir, on retrouve le calme de la campagne.",
      practicalTitle: "Au village, à pied",
      stationNote: "gare à 500 m",
      practical: [
        "Épicerie en face de la maison",
        "Pharmacie à 250 mètres",
        "Pizzeria et restaurant à 50 mètres",
        "Poste et distributeur",
        "Gare ferroviaire à 500 m",
        "Bus pour Trévise, Montebelluna et Valdobbiadene",
      ],
      places: [
        { name: "Trévise", time: "12 min", text: "Ville d'eau et d'arcades : les canaux Buranelli, la Piazza dei Signori, le marché aux poissons et l'apéritif au Prosecco DOC dans les tavernes du centre." },
        { name: "Venise", time: "1 h de train", text: "Depuis la gare de Postioma, le train mène directement à Venezia Santa Lucia. La Sérénissime pour une journée, sans souci de stationnement." },
        { name: "Collines du Prosecco", time: "30 min", text: "Conegliano et Valdobbiadene, patrimoine UNESCO : caves, routes des vins et collines qui semblent peintes. Dégustations partout." },
        { name: "Asolo et les villages", time: "30 min", text: "Asolo, la ville aux cent horizons, et l'Abbaye de Follina : des joyaux à trente minutes, entre villas vénitiennes et paysages de carte postale." },
        { name: "Dolomites", time: "1 h", text: "Le Parc National des Dolomites de Belluno est à une heure en voiture : randonnées, refuges et panoramas infinis pour une journée en montagne." },
      ],
      cta: "Réservez votre base en Vénétie",
    },
    reviewsPage: {
      overline: "Les avis",
      title: "De vraies paroles de vrais invités",
      sub: "Avis publiés par nos invités sur Airbnb au fil des années.",
      verified: "via Airbnb",
      showMore: "Voir tous les avis",
      showLess: "Voir moins",
      cards: [
        { name: "Postumia Place", rating: "4.83", count: "47 avis" },
        { name: "Postumia Place Junior", rating: "4.81", count: "213 avis" },
        { name: "Elio · votre hôte", rating: "4.82", count: "260 avis" },
      ],
      reviews: [],
      cta: "Venez écrire le prochain avis",
    },
    unitsPage: {
      overline: "Les logements",
      title: "Nos logements à Postioma di Paese",
      sub: "Appartements indépendants dans la même maison à Postioma, avec cour, jardin et parking réservé. Pour les couples, les familles et les voyageurs d'affaires.",
      discover: "Découvrez le logement",
      guests: "invités",
      bedrooms: "chambres",
      beds: "lits",
      bathrooms: "salle de bain",
      studio: "studio",
      sqm: "m²",
    },
    contactPage: {
      overline: "B&B · Postioma di Paese",
      title: "Contact",
      sub: "Questions, demandes spéciales, séjours professionnels ou longs : écrivez-nous, nous répondons dans les plus brefs délais.",
      form: {
        name: "Nom et prénom",
        email: "Email",
        message: "Message",
        messagePlaceholder: "Comment pouvons-nous vous aider ?",
        submit: "Envoyer le message",
        sending: "Envoi en cours…",
      },
      success: {
        title: "Message envoyé !",
        text: "Merci {name} : nous vous répondrons dans les plus brefs délais à {email}.",
        newMessage: "Envoyer un autre message",
      },
      errors: {
        name: "Indiquez votre nom.",
        email: "Indiquez une adresse email valide.",
        message: "Écrivez un message.",
        generic: "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
      },
      infoTitle: "Nos contacts",
      response: "Réponse rapide · 9 ans d'hospitalité",
    },
    wherePage: {
      overline: "Où nous sommes",
      title: "À 12 minutes de Trévise",
      sub: "Postumia Place se trouve à Postioma di Paese, dans la province de Trévise : un village tranquille avec tous les services à deux pas et toute la Vénétie à portée de main.",
      addressTitle: "L'adresse",
      address: "Via Fratelli Bianchin 13 · 31038 Postioma di Paese (TV) · Vénétie, Italie",
      howTitle: "Comment venir",
      how: [
        { icon: "Plane", title: "En avion", text: "Aéroport de Trévise Canova à 15 minutes en voiture ; Venise Marco Polo à environ 30 minutes." },
        { icon: "TrainFront", title: "En train", text: "La gare ferroviaire de Postioma est à 500 mètres : Trévise en quelques minutes, Venise en environ une heure." },
        { icon: "Car", title: "En voiture", text: "À environ 10 minutes des sorties de Trévise. Parking intérieur gratuit et réservé aux invités." },
      ],
      distancesTitle: "Les distances",
      distances: ["Trévise · 12 min", "Aéroport TSF · 15 min", "Venise · 1 h", "Conegliano · 30 min", "Asolo · 30 min", "Dolomites · 1 h"],
      mapLabel: "Carte de Postioma di Paese",
    },
    booking: {
      overline: "Réservation directe",
      title: "Réservez votre séjour",
      seo: bookingSeo.fr,
      sub: "Envoyez votre demande et nous répondrons dans les plus brefs délais.",
      howTitle: "Comment ça marche",
      steps: [
        "Choisissez le logement et les dates",
        "Envoyez votre demande, sans engagement",
        "Nous confirmons la disponibilité",
      ],
      contactTitle: "Contacts directs",
      times: "Arrivée de 15h00 à 20h00 · Départ avant 11h00",
      form: {
        unit: "Choisissez le logement",
        dates: "Vos dates",
        datesHint: "Sélectionnez l'arrivée et le départ",
        arrival: "Arrivée",
        departure: "Départ",
        pickArrival: "Touchez le jour d'arrivée",
        pickDeparture: "Touchez maintenant le jour de départ",
        changeDates: "Modifier les dates",
        legendStay: "Votre séjour",
        legendBooked: "Déjà réservé",
        notChosen: "À choisir",
        nights: "nuits",
        night: "nuit",
        priceTitle: "Votre prix",
        priceTotal: "Total",
        guests: "Invités",
        maxGuests: "max",
        name: "Nom et prénom",
        namePlaceholder: "Jean Dupont",
        email: "Email",
        emailPlaceholder: "jean@exemple.fr",
        phone: "Téléphone / WhatsApp (facultatif)",
        phonePlaceholder: "+33 6 00 00 00 00",
        message: "Message (facultatif)",
        messagePlaceholder: "Heure d'arrivée, besoins particuliers, questions…",
        submit: "Envoyer la demande",
        prevMonth: "Mois précédent",
        nextMonth: "Mois suivant",
        guestsLess: "Retirer un voyageur",
        guestsMore: "Ajouter un voyageur",
        sending: "Envoi en cours…",
      },
      errors: {
        dates: "Sélectionnez les dates de votre séjour.",
        rangeBlocked: "La période choisie comprend des dates déjà réservées : choisissez une autre date de départ ou d'autres dates.",
        name: "Indiquez votre nom.",
        email: "Indiquez une adresse email valide.",
        unavailable: "Ces dates ne sont plus disponibles pour le logement choisi. Essayez d'autres dates ou l'autre logement.",
        generic: "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
      },
      success: {
        title: "Demande envoyée !",
        text: "Merci {name} : nous avons reçu votre demande pour {unit}. Nous vous répondrons dans les plus brefs délais à {email} pour confirmer la disponibilité.",
        summary: "Votre récapitulatif",
        guests: "invités",
        newRequest: "Envoyer une autre demande",
      },
    },
    footer: {
      tagline: "Une cour, la campagne vénitienne tout autour. À 12 minutes de Trévise, une heure de Venise.",
      contacts: "Contacts",
      explore: "Explorer",
      unitsTitle: "Les logements",
      address: "Via Fratelli Bianchin 13 · Postioma di Paese (TV) · Vénétie · Italie",
      cin: "CIN : IT026055B4BEMQDC2F",
      rights: "Tous droits réservés.",
      email: "info@postumiaplace.it",
      phone: "+39 349 418 4040",
      whatsapp: "+39 349 418 4040",
    },
  },
  es: {
    nav: {
      home: "Inicio",
      units: "Los Alojamientos",
      surroundings: "Alrededores",
      reviews: "Opiniones",
      book: "Reservar",
      where: "Dónde Estamos",
      contact: "Contacto",
      menuOpen: "Abrir el menú",
      menuClose: "Cerrar el menú",
      skip: "Saltar al contenido",
    },
    gallery: { close: "Cerrar", prev: "Foto anterior", next: "Foto siguiente" },
    notFound: { title: "Página no encontrada", text: "La dirección que has abierto no existe o ha cambiado.", cta: "Volver al inicio", seoTitle: "Página no encontrada | Postumia Place" },
    hero: {
      overline: "B&B · Postioma di Paese · Treviso",
      lines: ["Una estancia tranquila", "en Postioma di Paese, entre Treviso y Venecia"],
      sub: "Apartamentos independientes en la tranquilidad de la campiña de Treviso: a 12 minutos de Treviso, una hora en tren de Venecia, con los Dolomitas en el horizonte.",
      ctaPrimary: "Reserva tu estancia",
      ctaSecondary: "Descubre los alojamientos",
      scroll: "Desplázate",
    },
    marquee: ["Descanso", "Descubrimiento", "Sabores", "Naturaleza", "Hospitalidad"],
    stats: [
      { value: "4.8+", label: "valoración media" },
      { value: "260", label: "opiniones verificadas" },
      { value: "9", label: "años de hospitalidad" },
    ],
    homeUnits: {
      overline: "Los apartamentos",
      title: "Elige dónde alojarte",
      all: "Ver todos",
      discover: "Descubre el apartamento",
      prev: "Apartamentos anteriores",
      next: "Apartamentos siguientes",
      verified: "Reseña verificada",
      reviews: "Opiniones de los huéspedes",
      prevReview: "Reseña anterior",
      nextReview: "Reseña siguiente",
    },
    manifesto: {
      overline: "Por qué Postumia Place",
      title: "Tres razones para quedarse",
      chapters: [
        {
          n: "01",
          title: "La filosofía",
          text: "Apartamentos independientes, con entrada propia y sin horarios de recepción. Espacios luminosos, parqué y la tranquilidad de la campiña véneta.",
        },
        {
          n: "02",
          title: "La ubicación",
          text: "La estación de tren está a 500 metros. Treviso a 12 minutos, Venecia a una hora en tren, las colinas del Prosecco a media hora. Todo el Véneto al alcance de la mano.",
        },
        {
          n: "03",
          title: "La hospitalidad",
          text: "Elio recibe a cada huésped en persona desde hace 9 años. Check-in flexible, respuesta rápida y consejos sinceros sobre qué ver y dónde comer.",
        },
      ],
    },
    ecoBadge: {
      enabled: true,
      text: "100% energía solar",
    },
    unitsSection: {
      overline: "Los alojamientos",
      title: "Nuestros alojamientos en Postioma di Paese",
      discover: "Descubre más",
      guests: "huéspedes",
      bedrooms: "habitaciones",
      beds: "camas",
      bathrooms: "baño",
      studio: "estudio",
    },
    reviewTeaser: {
      overline: "Hablan de nosotros",
      quote: "Nos sentimos realmente como en casa.",
      author: "Rawane · junio de 2025",
      cta: "Lee todas las opiniones",
    },
    homeCta: {
      title: "Escríbenos para conocer la disponibilidad",
      sub: "Solicita tus fechas: respondemos lo antes posible, sin compromiso.",
      cta: "Consultar disponibilidad",
    },
    unitPage: {
      detailsTitle: "En detalle",
      fromPrice: "desde {price} € la noche",
      overline: "B&B · Postioma di Paese",
      reviews: "opiniones",
      bookThis: "Reserva este alojamiento",
      galleryTitle: "La galería",
      amenitiesTitle: "Qué encontrarás",
      infoTitle: "Bueno saberlo",
      checkin: "Entrada",
      checkout: "Salida",
      checkinValue: "de 15:00 a 20:00",
      checkoutValue: "antes de las 11:00",
      maxGuests: "Huéspedes máximos",
      selfCheckin: "Llegada autónoma",
      selfCheckinValue: "Self check-in sencillo",
      nearbyTitle: "A pocos pasos",
      nearby: [
        "Tienda de alimentación frente a la casa",
        "Farmacia a 250 m",
        "Pizzería y restaurante a 50 m",
        "Estación de tren a 500 m",
        "Parada de autobús a 5 minutos",
        "Correos y cajero en el pueblo",
      ],
      otherUnit: "Descubre también",
      sleepTitle: "Dónde dormirás",
      accessTitle: "Acceso para huéspedes",
      notesTitle: "Otras cosas que recordar",
      reviewsTitle: "Opiniones de los huéspedes",
      verified: "vía Airbnb",
      showMore: "Muestra todas las opiniones",
      showLess: "Muestra menos",
      showAllAmenities: "Muestra todos los servicios ·",
      showLessAmenities: "Muestra menos",
      notAvailableTitle: "No disponibles en este alojamiento",
      categories: {
        cleanliness: "Limpieza",
        accuracy: "Precisión",
        checkin: "Llegada",
        communication: "Comunicación",
        location: "Ubicación",
        value: "Relación calidad-precio",
      },
      guests: "huéspedes",
      bedrooms: "habitaciones",
      beds: "camas",
      bathrooms: "baño",
      sqm: "m²",
      unavailable: "No disponible",
      comingSoon: "Próximamente disponible",
    },
    unitStatus: {
      available: { label: "Disponible", hint: "Reserva online" },
      comingSoon: { label: "Próximamente", hint: "Aún no se puede reservar" },
      unavailable: { label: "No disponible", hint: "Por ahora no se puede reservar" },
    },
    dintorni: {
      overline: "Los alrededores",
      title: "El Véneto para vivirlo",
      sub: "Postioma es un punto de partida perfecto: ciudades de arte, colinas del Prosecco, pueblos y montañas. Y por la noche, se vuelve a la tranquilidad del campo.",
      practicalTitle: "En el pueblo, a pie",
      stationNote: "estación a 500 m",
      practical: [
        "Tienda de alimentación frente a la casa",
        "Farmacia a 250 metros",
        "Pizzería y restaurante a 50 metros",
        "Oficina de correos y cajero",
        "Estación de tren a 500 m",
        "Autobús a Treviso, Montebelluna y Valdobbiadene",
      ],
      places: [
        { name: "Treviso", time: "12 min", text: "Ciudad de agua y arcadas: los canales Buranelli, la Piazza dei Signori, el mercado de pescado y el aperitivo con Prosecco DOC en las tabernas del centro." },
        { name: "Venecia", time: "1 h en tren", text: "Desde la estación de Postioma el tren lleva directamente a Venezia Santa Lucia. La Serenísima por un día, sin preocuparte por aparcar." },
        { name: "Colinas del Prosecco", time: "30 min", text: "Conegliano y Valdobbiadene, patrimonio UNESCO: bodegas, rutas del vino y colinas que parecen pintadas. Catas por todas partes." },
        { name: "Asolo y los pueblos", time: "30 min", text: "Asolo, la ciudad de los cien horizontes, y la Abadía de Follina: joyas a media hora en coche, entre villas venecianas y paisajes de postal." },
        { name: "Dolomitas", time: "1 h", text: "El Parque Nacional de las Dolomitas de Belluno está a una hora en coche: senderismo, refugios y panoramas infinitos para un día de montaña." },
      ],
      cta: "Reserva tu base en el Véneto",
    },
    reviewsPage: {
      overline: "Las opiniones",
      title: "Palabras auténticas de huéspedes reales",
      sub: "Opiniones publicadas por nuestros huéspedes en Airbnb a lo largo de los años.",
      verified: "vía Airbnb",
      showMore: "Muestra todas las opiniones",
      showLess: "Muestra menos",
      cards: [
        { name: "Postumia Place", rating: "4.83", count: "47 opiniones" },
        { name: "Postumia Place Junior", rating: "4.81", count: "213 opiniones" },
        { name: "Elio · tu anfitrión", rating: "4.82", count: "260 opiniones" },
      ],
      reviews: [],
      cta: "Ven a escribir la próxima opinión",
    },
    unitsPage: {
      overline: "Los alojamientos",
      title: "Nuestros alojamientos en Postioma di Paese",
      sub: "Apartamentos independientes en la misma casa de Postioma, con patio, jardín y aparcamiento reservado. Para parejas, familias y viajeros de trabajo.",
      discover: "Descubre el alojamiento",
      guests: "huéspedes",
      bedrooms: "habitaciones",
      beds: "camas",
      bathrooms: "baño",
      studio: "estudio",
      sqm: "m²",
    },
    contactPage: {
      overline: "B&B · Postioma di Paese",
      title: "Contacto",
      sub: "Preguntas, peticiones especiales, estancias de trabajo o largas: escríbenos, respondemos lo antes posible.",
      form: {
        name: "Nombre y apellidos",
        email: "Email",
        message: "Mensaje",
        messagePlaceholder: "¿Cómo podemos ayudarte?",
        submit: "Enviar mensaje",
        sending: "Enviando…",
      },
      success: {
        title: "¡Mensaje enviado!",
        text: "Gracias {name}: te responderemos lo antes posible a {email}.",
        newMessage: "Enviar otro mensaje",
      },
      errors: {
        name: "Introduce tu nombre.",
        email: "Introduce un email válido.",
        message: "Escribe un mensaje.",
        generic: "Algo ha salido mal. Vuelve a intentarlo o escríbenos directamente.",
      },
      infoTitle: "Nuestros contactos",
      response: "Respuesta rápida · 9 años de hospitalidad",
    },
    wherePage: {
      overline: "Dónde estamos",
      title: "A 12 minutos de Treviso",
      sub: "Postumia Place se encuentra en Postioma di Paese, provincia de Treviso: un pueblo tranquilo con todos los servicios a pocos pasos y todo el Véneto al alcance de la mano.",
      addressTitle: "La dirección",
      address: "Via Fratelli Bianchin 13 · 31038 Postioma di Paese (TV) · Véneto, Italia",
      howTitle: "Cómo llegar",
      how: [
        { icon: "Plane", title: "En avión", text: "Aeropuerto de Treviso Canova a 15 minutos en coche; Venecia Marco Polo a unos 30 minutos." },
        { icon: "TrainFront", title: "En tren", text: "La estación de Postioma está a 500 metros: Treviso en pocos minutos, Venecia en aproximadamente una hora." },
        { icon: "Car", title: "En coche", text: "A unos 10 minutos de las salidas de Treviso. Aparcamiento interior gratuito y reservado para huéspedes." },
      ],
      distancesTitle: "Las distancias",
      distances: ["Treviso · 12 min", "Aeropuerto TSF · 15 min", "Venecia · 1 h", "Conegliano · 30 min", "Asolo · 30 min", "Dolomitas · 1 h"],
      mapLabel: "Mapa de Postioma di Paese",
    },
    booking: {
      overline: "Reserva directa",
      title: "Reserva tu estancia",
      seo: bookingSeo.es,
      sub: "Envía tu solicitud y te responderemos lo antes posible.",
      howTitle: "Cómo funciona",
      steps: [
        "Elige el alojamiento y las fechas",
        "Envía tu solicitud, sin compromiso",
        "Te confirmamos la disponibilidad",
      ],
      contactTitle: "Contactos directos",
      times: "Entrada de 15:00 a 20:00 · Salida antes de las 11:00",
      form: {
        unit: "Elige el alojamiento",
        dates: "Tus fechas",
        datesHint: "Selecciona entrada y salida",
        arrival: "Llegada",
        departure: "Salida",
        pickArrival: "Toca el día de llegada",
        pickDeparture: "Ahora toca el día de salida",
        changeDates: "Cambiar fechas",
        legendStay: "Tu estancia",
        legendBooked: "Ya reservado",
        notChosen: "Por elegir",
        nights: "noches",
        night: "noche",
        priceTitle: "Tu precio",
        priceTotal: "Total",
        guests: "Huéspedes",
        maxGuests: "máx",
        name: "Nombre y apellidos",
        namePlaceholder: "Juan Pérez",
        email: "Email",
        emailPlaceholder: "juan@ejemplo.es",
        phone: "Teléfono / WhatsApp (opcional)",
        phonePlaceholder: "+34 600 00 00 00",
        message: "Mensaje (opcional)",
        messagePlaceholder: "Hora de llegada, necesidades especiales, preguntas…",
        submit: "Enviar solicitud",
        prevMonth: "Mes anterior",
        nextMonth: "Mes siguiente",
        guestsLess: "Quitar un huésped",
        guestsMore: "Añadir un huésped",
        sending: "Enviando…",
      },
      errors: {
        dates: "Selecciona las fechas de tu estancia.",
        rangeBlocked: "El periodo elegido incluye fechas ya ocupadas: elige otra fecha de salida u otras fechas.",
        name: "Introduce tu nombre.",
        email: "Introduce un email válido.",
        unavailable: "Estas fechas ya no están disponibles para el alojamiento elegido. Prueba otras fechas o el otro alojamiento.",
        generic: "Algo ha salido mal. Vuelve a intentarlo o escríbenos directamente.",
      },
      success: {
        title: "¡Solicitud enviada!",
        text: "Gracias {name}: hemos recibido tu solicitud para {unit}. Te responderemos lo antes posible a {email} para confirmar la disponibilidad.",
        summary: "Tu resumen",
        guests: "huéspedes",
        newRequest: "Enviar otra solicitud",
      },
    },
    footer: {
      tagline: "Un patio, la campiña véneta alrededor. A 12 minutos de Treviso, a una hora de Venecia.",
      contacts: "Contacto",
      explore: "Explora",
      unitsTitle: "Los alojamientos",
      address: "Via Fratelli Bianchin 13 · Postioma di Paese (TV) · Véneto · Italia",
      cin: "CIN: IT026055B4BEMQDC2F",
      rights: "Todos los derechos reservados.",
      email: "info@postumiaplace.it",
      phone: "+39 349 418 4040",
      whatsapp: "+39 349 418 4040",
    },
  },
};

function mergeLang(base, t) {
  return {
    ...base,
    hero: { ...base.hero, overline: t.hero.overline, lines: t.hero.lines, sub: t.hero.sub },
    marquee: t.marquee,
    seo: t.seo,
    manifesto: { ...base.manifesto, overline: t.manifesto.overline, title: t.manifesto.title, chapters: t.manifesto.chapters },
    homeUnits: { ...base.homeUnits, overline: t.homeUnits?.overline ?? base.homeUnits.overline, title: t.homeUnits?.title ?? base.homeUnits.title },
    ecoBadge: { ...base.ecoBadge, enabled: t.ecoBadge.enabled, text: t.ecoBadge.text },
    unitsSection: { ...base.unitsSection, title: t.unitsSection.title },
    unitsPage: { ...base.unitsPage, title: t.unitsSection.title, sub: t.unitsPage.sub },
    homeCta: { ...base.homeCta, title: t.homeCta.title },
    reviewTeaser: { ...base.reviewTeaser, quote: t.reviewTeaser.quote, author: t.reviewTeaser.author },
    dintorni: { ...base.dintorni, title: t.dintorni.title, sub: t.dintorni.sub, practical: t.dintorni.practical, places: t.dintorni.places },
    footer: { ...base.footer, tagline: t.footer.tagline, address: t.footer.address, email: t.footer.email, phone: t.footer.phone, whatsapp: t.footer.whatsapp, cin: t.footer.cin },
    contactPage: { ...base.contactPage, title: t.contactPage.title, sub: t.contactPage.sub },
  };
}

export const translations = {
  it: mergeLang(baseTranslations.it, testi.it),
  en: mergeLang(baseTranslations.en, testi.en),
  fr: mergeLang(baseTranslations.fr, testi.fr),
  es: mergeLang(baseTranslations.es, testi.es),
};

export const SUPPORTED_LANGS = ["it", "en", "fr", "es"];
// "it" non ha prefisso di path (è la lingua di default) — solo le altre lo hanno.
export const PREFIXED_LANGS = SUPPORTED_LANGS.filter((l) => l !== "it");

// Il segmento dopo il primo "/" è "en"/"fr" solo se è uno dei prefissi
// gestiti — altrimenti (assente, o qualsiasi altro slug) la lingua è IT.
export function detectLangFromPath(pathname) {
  const seg = pathname.split("/")[1];
  return PREFIXED_LANGS.includes(seg) ? seg : "it";
}

// Converte un path (con o senza prefisso lingua) nel path equivalente
// per `lang`. Slug identici in tutte le lingue: basta swap del prefisso.
export function withLangPath(pathname, lang) {
  const seg = pathname.split("/")[1];
  const bare = PREFIXED_LANGS.includes(seg) ? (pathname.slice(seg.length + 1) || "/") : pathname;
  if (lang === "it") return bare;
  return `/${lang}${bare === "/" ? "" : bare}`;
}

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLangFromPath(location.pathname);

  const setLang = (l) => {
    try {
      localStorage.setItem("pp-lang", l);
    } catch {}
    navigate(withLangPath(location.pathname, l) + location.search + location.hash);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
